import React from "react";
import moment from "moment";
import {
  Box, IconButton, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Delete02Icon, PencilEdit01Icon} from "@hugeicons/core-free-icons";
import {SERVICE_FIELDS} from "../serviceFields";

const COLUMNS = ["NAME", "SERVICES", "ACCESS", "DATE UPDATED"];

const headCellSx = {
  height: 40, py: 0.5, px: 1.5, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0",
  bgcolor: "#FAFAFD", borderBottom: "1px solid #ECECF3", whiteSpace: "nowrap",
};
const cellSx = {py: 1.25, px: 1.5, fontSize: 13, color: "#23264D", borderBottom: "1px solid #F2F2F7", verticalAlign: "top"};
const iconButtonSx = {borderRadius: "50% !important", color: "#8A8AA0", "&:hover": {bgcolor: "#F4F4FA", color: "#4A4A63"}};
const deleteIconButtonSx = {...iconButtonSx, "&:hover": {bgcolor: "rgba(220, 38, 38, 0.08)", color: "#DC2626"}};

export const BackendConfigurationsTable = ({configurations, loading, emptyText, getOrgName, canManage, onEdit, onDelete}) => {
  const columnCount = COLUMNS.length + (canManage ? 1 : 0);

  return (
    <TableContainer sx={{width: "100%", overflowX: "auto"}}>
      <Table size={"small"} sx={{minWidth: 820}}>
        <TableHead>
          <TableRow>
            {COLUMNS.map((column, index) => (
              <TableCell key={column} sx={{...headCellSx, ...(index === 0 && {pl: 2.5})}}>{column}</TableCell>
            ))}
            {canManage && <TableCell sx={{...headCellSx, pr: 2.5}}/>}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && Array.from({length: 4}).map((_, index) => (
            <TableRow key={index}>
              <TableCell colSpan={columnCount} sx={{...cellSx, px: 2.5}}><Skeleton height={32}/></TableCell>
            </TableRow>
          ))}
          {!loading && configurations.length === 0 && (
            <TableRow>
              <TableCell colSpan={columnCount} sx={{...cellSx, borderBottom: 0, py: 6, textAlign: "center", color: "#8A8AA0"}}>
                {emptyText}
              </TableCell>
            </TableRow>
          )}
          {!loading && configurations.map(configuration => (
            <BackendConfigurationRow
              key={configuration.id}
              configuration={configuration}
              orgName={getOrgName(configuration.organizationId)}
              canManage={canManage}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const BackendConfigurationRow = ({configuration, orgName, canManage, onEdit, onDelete}) => {
  const services = SERVICE_FIELDS.filter(field => configuration[field.name]);

  return (
    <TableRow hover sx={{"&.MuiTableRow-hover:hover": {bgcolor: "#FAFAFD"}}}>
      <TableCell sx={{...cellSx, pl: 2.5, fontWeight: 600}}>{configuration.name || "—"}</TableCell>
      <TableCell sx={cellSx}>
        {services.length === 0 && "—"}
        {services.map(field => (
          <Box key={field.name} sx={{maxWidth: 420, "&:not(:last-of-type)": {mb: 0.75}}}>
            <Typography sx={{display: "block", fontSize: 11, color: "#8A8AA0"}}>{field.label}</Typography>
            <Typography noWrap title={configuration[field.name]} sx={{fontFamily: "monospace", fontSize: 12.5, color: "inherit"}}>
              {configuration[field.name]}
            </Typography>
          </Box>
        ))}
      </TableCell>
      <TableCell sx={cellSx}>
        <Box component={"span"} sx={{
          px: 1, py: 0.25, borderRadius: "999px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
          color: configuration.isPublic ? "#6B6B80" : "#4B55C9",
          bgcolor: configuration.isPublic ? "#F2F2F7" : "rgba(132, 145, 247, 0.12)",
        }}>
          {orgName}
        </Box>
      </TableCell>
      <TableCell sx={{...cellSx, color: "#6B6B80", whiteSpace: "nowrap"}}>
        {configuration.dateUpdated ? moment(configuration.dateUpdated).format("DD MMM YYYY HH:mm") : "—"}
      </TableCell>
      {canManage && (
        <TableCell sx={{...cellSx, pr: 2.5, whiteSpace: "nowrap"}}>
          <Stack direction={"row"} gap={0.25} justifyContent={"flex-end"}>
            <Tooltip title={"Edit"}>
              <IconButton size={"small"} sx={iconButtonSx} onClick={() => onEdit(configuration)}>
                <HugeiconsIcon icon={PencilEdit01Icon} size={16}/>
              </IconButton>
            </Tooltip>
            <Tooltip title={"Delete"}>
              <IconButton size={"small"} sx={deleteIconButtonSx} onClick={() => onDelete(configuration)}>
                <HugeiconsIcon icon={Delete02Icon} size={16}/>
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      )}
    </TableRow>
  );
};
